import { z } from "zod";
import { discordSnowflakeSchema, utcTimestampSchema, } from "./guild-settings.js";
import { uuidSchema } from "./platform-common.js";
export const USAGE_CONTRACT_VERSION = 1;
export const MAX_USAGE_REQUEST_LIMIT = 1_000_000_000;
export const MAX_USAGE_TOKENS_PER_REQUEST = 1_000_000_000;
export const MAX_USAGE_COST_MICRO_USD_PER_REQUEST = 1_000_000_000_000;
export const usageLimitModeSchema = z.enum(["observe", "enforce"]);
export const usageScopeTypeSchema = z.enum(["guild", "dm_user"]);
export const usageReservationStatusSchema = z.enum([
    "reserved",
    "succeeded",
    "failed",
]);
export const usageSettlementOutcomeSchema = z.enum(["succeeded", "failed"]);
export const usageDenialReasonSchema = z.enum([
    "entitlement_denied",
    "usage_limit_exceeded",
]);
const usageReservationBase = {
    contractVersion: z.literal(USAGE_CONTRACT_VERSION),
    correlationId: uuidSchema,
    requestId: discordSnowflakeSchema,
    requestLimit: z.int().min(0).max(MAX_USAGE_REQUEST_LIMIT),
    limitMode: usageLimitModeSchema,
};
export const usageReservationInputSchema = z.discriminatedUnion("scopeType", [
    z
        .object({
        ...usageReservationBase,
        scopeType: z.literal("guild"),
        guildId: discordSnowflakeSchema,
    })
        .strict(),
    z
        .object({
        ...usageReservationBase,
        scopeType: z.literal("dm_user"),
        userId: discordSnowflakeSchema,
    })
        .strict(),
]);
export const usageReservationDecisionSchema = z
    .object({
    contractVersion: z.literal(USAGE_CONTRACT_VERSION),
    correlationId: uuidSchema,
    scopeType: usageScopeTypeSchema,
    scopeId: discordSnowflakeSchema,
    admitted: z.boolean(),
    created: z.boolean(),
    execute: z.boolean(),
    denialReason: usageDenialReasonSchema.nullable(),
    entitlementState: z
        .enum(["trial", "subscribed", "complimentary"])
        .nullable(),
    status: usageReservationStatusSchema.nullable(),
    monthStartedAt: utcTimestampSchema,
    requestCount: z.int().nonnegative(),
    requestLimit: z.int().min(0).max(MAX_USAGE_REQUEST_LIMIT),
    limitMode: usageLimitModeSchema,
    wouldExceed: z.boolean(),
    reservedAt: utcTimestampSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.execute !== value.created || (value.created && !value.admitted)) {
        context.addIssue({
            code: "custom",
            message: "Only a newly created admitted reservation can execute",
            path: ["execute"],
        });
    }
    if (value.admitted !== (value.status !== null)) {
        context.addIssue({
            code: "custom",
            message: "Only admitted usage can have a reservation status",
            path: ["status"],
        });
    }
    if (value.admitted === (value.denialReason !== null)) {
        context.addIssue({
            code: "custom",
            message: "Denied usage must have exactly one denial reason",
            path: ["denialReason"],
        });
    }
    if (value.scopeType === "guild" &&
        value.admitted &&
        value.entitlementState === null) {
        context.addIssue({
            code: "custom",
            message: "Admitted guild usage requires an effective entitlement",
            path: ["entitlementState"],
        });
    }
    if (value.scopeType === "dm_user" && value.entitlementState !== null) {
        context.addIssue({
            code: "custom",
            message: "DM usage cannot carry a guild entitlement",
            path: ["entitlementState"],
        });
    }
});
export const usageSettlementInputSchema = z
    .object({
    contractVersion: z.literal(USAGE_CONTRACT_VERSION),
    correlationId: uuidSchema,
    outcome: usageSettlementOutcomeSchema,
    inputTokens: z.int().min(0).max(MAX_USAGE_TOKENS_PER_REQUEST),
    outputTokens: z.int().min(0).max(MAX_USAGE_TOKENS_PER_REQUEST),
    totalTokens: z.int().min(0).max(MAX_USAGE_TOKENS_PER_REQUEST),
    costMicroUsd: z
        .int()
        .min(0)
        .max(MAX_USAGE_COST_MICRO_USD_PER_REQUEST)
        .default(0),
})
    .strict()
    .refine((value) => value.totalTokens >= value.inputTokens + value.outputTokens, {
    message: "Total tokens cannot be less than input plus output tokens",
    path: ["totalTokens"],
});
export const usageSettlementSchema = z
    .object({
    contractVersion: z.literal(USAGE_CONTRACT_VERSION),
    correlationId: uuidSchema,
    status: usageSettlementOutcomeSchema,
    settledAt: utcTimestampSchema,
    inputTokens: z.int().nonnegative(),
    outputTokens: z.int().nonnegative(),
    totalTokens: z.int().nonnegative(),
    costMicroUsd: z.int().nonnegative(),
})
    .strict();
export const guildUsageProjectionSchema = z
    .object({
    contractVersion: z.literal(USAGE_CONTRACT_VERSION),
    guildId: discordSnowflakeSchema,
    monthStartedAt: utcTimestampSchema,
    monthEndsAt: utcTimestampSchema,
    requestCount: z.int().nonnegative(),
    succeededCount: z.int().nonnegative(),
    failedCount: z.int().nonnegative(),
    reservedCount: z.int().nonnegative(),
    wouldExceedCount: z.int().nonnegative(),
    inputTokens: z.int().nonnegative(),
    outputTokens: z.int().nonnegative(),
    totalTokens: z.int().nonnegative(),
    costMicroUsd: z.int().nonnegative(),
    updatedAt: utcTimestampSchema.nullable(),
})
    .strict()
    .refine((value) => value.requestCount ===
    value.succeededCount + value.failedCount + value.reservedCount, {
    message: "Request outcomes must equal admitted requests",
    path: ["requestCount"],
});
export const usageRecoveryResultSchema = z
    .object({
    contractVersion: z.literal(USAGE_CONTRACT_VERSION),
    recoveredCount: z.int().min(0).max(500),
})
    .strict();
//# sourceMappingURL=usage.js.map